<template>
  <SettingsLayout>
    <div class="accommodations-view">
      <div class="container">
        <section>
          <unit-type-modal ref="newUnitTypeModal" />
          <unit-modal ref="unitModal" />

          <section v-if="isRequestingUnits || isRequestingUnitTypes">
            <spinner-loader
              class="centerscreen"
              :loading="isRequestingUnits || isRequestingUnitTypes"
            />
          </section>

          <section v-else-if="errored">
            <p>An error occurred.</p>
          </section>

          <section v-else class="content-container">
            <!-- Search and Add New Unit Type button -->
            <b-row class="mb-4" align-h="between">
              <b-col md="6">
                <b-input-group>
                  <b-form-input
                    v-model="filter"
                    :placeholder="$t('filter_cat')"
                  />
                </b-input-group>
              </b-col>
              <b-col md="4" class="text-right">
                <b-button variant="primary" @click="showNewUnitTypeModal">
                  <i class="fi-plus mr-1"></i>
                  {{ $t("settings.accommodations.unit_type.create") }}
                </b-button>
              </b-col>
            </b-row>

            <!-- Accordion for Unit Types -->
            <div v-if="visibleUnitTypes.length > 0">
              <b-card
                v-for="(unitType, index) in visibleUnitTypes"
                :key="unitType.id"
                no-body
                class="mb-3 unit-type-accordion"
              >
                <!-- Accordion Header -->
                <b-card-header
                  class="d-flex justify-content-between align-items-center p-3"
                  :class="{
                    'unit-type-header-active': activeAccordion === index,
                  }"
                  @click="toggleAccordion(index)"
                  header-tag="header"
                  role="button"
                >
                  <div>
                    <h5 class="mb-0">{{ unitType.unit_type_name }}</h5>
                    <div class="text-muted small">
                      {{ unitType.unit_type_description }}
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="mr-4 font-weight-bold">
                      {{ getVendor.local_currency }}{{ unitType.base_rate }}/{{
                        $t("night")
                      }}
                    </span>
                    <i
                      class="accordion-icon"
                      :class="
                        activeAccordion === index
                          ? 'fi-chevron-up'
                          : 'fi-chevron-down'
                      "
                    ></i>
                  </div>
                </b-card-header>

                <!-- Accordion Content -->
                <b-collapse :visible="activeAccordion === index">
                  <b-card-body class="p-0">
                    <!-- Unit Type Actions Row -->
                    <div class="unit-type-content">
                      <!-- Action bar: title, edit unit-type and add room -->
                      <div
                        class="unit-type-actions d-flex justify-content-between align-items-center mb-3"
                      >
                        <h6 class="mb-0"></h6>
                        <div>
                          <b-button
                            size="sm"
                            variant="outline-primary"
                            class="mr-2"
                            @click="editUnitType(unitType)"
                          >
                            <i class="fi-pencil mr-1"></i>
                            {{ $t("edit") }}
                          </b-button>
                          <b-button
                            size="sm"
                            variant="primary"
                            @click="showAddRoomModal(unitType.id)"
                          >
                            <i class="fi-plus mr-1"></i>
                            {{ $t("unit_add_room_button") }}
                          </b-button>
                        </div>
                      </div>

                      <!-- Rooms table -->
                      <div v-if="unitType.unit && unitType.unit.length > 0">
                        <table class="table rooms-table mb-0">
                          <thead>
                            <tr>
                              <th>{{ $t("name") }}</th>
                              <th>{{ $t("description") }}</th>
                              <th class="text-right">{{ $t("rate") }}</th>
                              <th class="text-right">{{ $t("actions") }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="room in unitType.unit" :key="room.id">
                              <td class="font-weight-bold">
                                {{ room.unit_name }}
                              </td>
                              <td class="text-muted">
                                {{ room.unit_description }}
                              </td>
                              <td class="text-right room-rate">
                                {{ getVendor.local_currency
                                }}{{ room.unit_rate }}/{{ $t("night") }}
                              </td>
                              <td class="text-right">
                                <b-button
                                  size="sm"
                                  variant="outline-primary"
                                  @click="editRoom(unitType.id, room)"
                                >
                                  <i class="fi-pencil"></i>
                                </b-button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Empty state when no rooms -->
                      <div v-else class="text-center p-4 no-rooms">
                        <p>{{ "No rooms added yet" }}</p>
                      </div>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>
            </div>

            <!-- Empty state when no unit types -->
            <b-card v-else class="text-center p-5">
              <p>
                {{ $t("no_unit_types_message") || "No room types added yet" }}
              </p>
              <b-button variant="primary" @click="showNewUnitTypeModal">
                <i class="fi-plus mr-1"></i>
                {{ $t("settings.accommodations.unit_type.create") }}
              </b-button>
            </b-card>
          </section>
        </section>
      </div>
    </div>
  </SettingsLayout>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import UnitTypeModal from "../../../../components/modals/UnitTypeModal";
import UnitModal from "../../../../components/modals/UnitModal";
import SpinnerLoader from "@/components/widgets/SpinnerLoader";
import SettingsLayout from "@/components/layouts/SettingsLayout.vue";

export default {
  data() {
    return {
      accommodations: this.$store.getters.getAccommodations,
      filter: "",
      activeAccordion: null,
    };
  },
  watch: {
    getUnitTypesList: {
      handler(newValue, oldValue) {
        this.accommodations = newValue;
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters(["getUnitTypesList", "getVendor"]),
    ...mapState({
      errored: false, // state => state.accommodations.errored,
      isRequestingUnits: (state) => state.accommodations.isRequestingUnits,
      isRequestingUnitTypes: (state) =>
        state.accommodations.isRequestingUnitTypes,
    }),
    visibleUnitTypes() {
      const search = this.filter ? this.filter.toLowerCase() : "";
      return this.getUnitTypesList.filter((unitType) => {
        if (!search) return true;
        const nameMatch = unitType.unit_type_name
          .toLowerCase()
          .includes(search);
        const descMatch = (unitType.unit_type_description || "")
          .toLowerCase()
          .includes(search);
        return nameMatch || descMatch;
      });
    },
  },
  components: {
    UnitTypeModal,
    UnitModal,
    SpinnerLoader,
    SettingsLayout,
  },
  methods: {
    toggleAccordion(index) {
      this.activeAccordion = this.activeAccordion === index ? null : index;
    },
    showNewUnitTypeModal() {
      this.$refs.newUnitTypeModal.edit();
    },
    editUnitType(unitType) {
      this.$refs.newUnitTypeModal.edit(unitType);
    },
    showAddRoomModal(unitTypeId) {
      this.$refs.unitModal.edit(unitTypeId);
    },
    editRoom(unitTypeId, room) {
      this.$refs.unitModal.edit(unitTypeId, room);
    },
  },
};
</script>

<style lang="scss">
.accommodations-view {
  padding-top: 20px;
  padding-bottom: 40px;
  background-color: #f5f7fa;
  width: 100%;
  margin-left: 0;

  .centerscreen {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .unit-type-accordion {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  }

  .unit-type-accordion .card-header {
    background-color: #ffffff;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #f8f9fa;
    }
  }

  .unit-type-header-active {
    background-color: #82c7eb !important;
    color: #fff;

    .text-muted {
      color: rgba(255, 255, 255, 0.7) !important;
    }
  }

  .room-rate {
    font-weight: 600;
    color: #82c7eb;
  }

  .rooms-container {
    background-color: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
  }

  .room-item {
    background-color: #f8f9fa;
  }

  .container {
    max-width: 100% !important;
  }

  .content-container {
    padding: 2rem;

    @media (max-width: 768px) {
      padding: 1rem;
    }
  }

  // NEW STYLES
  .unit-type-content {
    background-color: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 1.5rem;
  }

  .unit-type-actions h6 {
    font-weight: 600;
  }

  .rooms-table {
    thead {
      background-color: #f8f9fa;
    }
    th {
      border-top: none;
    }
    td,
    th {
      vertical-align: middle;
    }
  }
}
</style>
